import React, { useEffect, useState } from "react";
import { Grid, TextField } from "@material-ui/core";
import { Divider, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { blue } from "@material-ui/core/colors";
import RegularButton from "components/CustomButtons/Button";
import { AutocompleteComponent } from "components/Autocomplete/AutocompleteComponent";
import { useForm } from "hooks/useForm";
import { useFetchLocal } from "hooks/fetch-hooks/useFetchLocal";
import { useSelector, useDispatch } from "react-redux";
import { ModalV2 } from "components/Modal/ModalV2";
import { createRetentionOutput, getCustomerBalance } from "redux/actions/retentionOutputs/retentionOutputsActions";
import { formatNumber } from "helpers/getNumberFormats";
import { retentionsTypesOptions, retentionsTypes } from "helpers/retentionsDictionary";
import { newDate } from "helpers/dateTools";
import { addLabelArray } from "helpers/addLabelArray";
import CardTable from "components/CardTable/CardTable";
import { Add } from "@material-ui/icons";
import { api_get, PROYECT } from "services/api";

const inputRightAlign = { style: { textAlign: "right" } };
const itemStyle = { display: "flex", justifyContent: "flex-end" };
const BASE_URL = `${process.env.REACT_APP_URL}${PROYECT}`;

const fetchWholesalers = async (filter) => {
  const resp = await api_get(`${BASE_URL}/customers/wholesalers?filter=${filter}`);
  return addLabelArray(resp || [], "name", "lastname");
};

const formInitialValues = {
  inputValues: {
    searchCustomer: "",
    customer: null,
    balance: "",
    paymentsTotal: "",
    retentionsTotal: "",
    advance: "",
    total: "",
    type: null,
    comments: "",
    customersOpt: [],
  },
  required: {
    customer: { required: true },
    advance: { required: true },
    total: { required: true },
    type: { required: true },
    comments: { required: true },
  },
};

export function RetentionForm({
  open,
  onClose,
  station,
  month,
  year,
  isClose = false,
  isMultiple = false,
  data = null,
  title = "Nueva Salida de Retención",
  submitBtnText = "Guardar",
}) {
  const dispatch = useDispatch();
  const { isLoadingComp } = useSelector(({ search }) => search);
  const [movements, setMovements] = useState([]);

  const { values, onChange, setValues, validate, errors, onChangeNumberV2, reset } = useForm(
    formInitialValues.inputValues,
    formInitialValues.required
  );

  const {
    searchCustomer, customer, advance, total, type,
    balance, paymentsTotal, retentionsTotal, comments, customersOpt
  } = values;

  const [[fetchedCustomers, searching, success], fetchCustomers] = useFetchLocal(fetchWholesalers);

  useEffect(() => {
    if (fetchedCustomers) {
      const customersLabel = addLabelArray(fetchedCustomers, 'name', 'code')
      setValues({ customersOpt:  customersLabel});
    }
  }, [fetchedCustomers]);

  const handleSearchCustomers = (e) => {
    e?.preventDefault?.();
    if (!searchCustomer || searchCustomer.length < 3) return;
    fetchCustomers(searchCustomer);
  };

  const getBalance = () => {
    dispatch(getCustomerBalance({
      idCustomer: customer?.idCustomer || customer?.value,
      idStation: station?.id,
      month,
      year,
      onSuccess: ({ balance, paymentsTotal, retentionsTotal }) => {
        setValues({ balance, paymentsTotal, retentionsTotal, advance: "", total: "" });
      }
    }));
  };

  const handleAdvanceChange = (e) => {
    onChangeNumberV2(e, { decimals: 2 });
    const raw = e.target.value.replace(/[^\d.-]/g, "").replace(/(\..*)\./g, "$1");
    const advanceNum = parseFloat(raw) || 0;
    const balanceNum = parseFloat(balance) || 0;
    const totalCalc = balanceNum - advanceNum;
    setValues({ total: totalCalc.toFixed(2) });
  };

  const handleSubmit = () => {
    const optionalFields = isClose
      ? ["type", "advance", "total", "comments"]
      : isMultiple
        ? ["type", "total", "advance", "comments"]
        : [];
    validate(optionalFields, () => {
      if (isMultiple) {
        dispatch(createRetentionOutput(movements, onClose));
      } else if (isClose) {
        const body = {
          idCustomer: data?.idCustomer || customer?.idCustomer || customer?.value,
          idStation: station?.id,
          type: retentionsTypes.CLOSE,
          amount: parseFloat(balance) || 0,
          date: newDate(),
          periodMonth: month,
          periodYear: year,
          description: comments || "",
        };
        dispatch(createRetentionOutput(body, onClose));
      } else {
        const body = {
          idCustomer: customer?.idCustomer || customer?.value,
          idStation: station?.id,
          type: type?.value?.toUpperCase() || "",
          amount: parseFloat(advance) || 0,
          date: newDate(),
          periodMonth: month,
          periodYear: year,
          description: comments || "",
        };
        dispatch(createRetentionOutput(body, onClose));
      }
    });
  };

  useEffect(() => {
    if (!isMultiple && customer) getBalance();
  }, [customer]);

  useEffect(() => {
    if (data && isMultiple) {
      setValues({
        customer: { label: data.customerName, value: data.idCustomer, idCustomer: data.idCustomer, idAccount: data.idAccount },
        searchCustomer: data.customerName,
        retentionsTotal: data.retentions || 0,
        paymentsTotal: data.advances || 0,
        balance: data.monthlyBalance || 0,
      });
    } else if (data && !isMultiple) {
      setValues({
        customer: { label: data.customerName, value: data.idCustomer, idCustomer: data.idCustomer },
        searchCustomer: data.customerName,
      });
    } else {
      reset();
    }
    setMovements([]);
  }, [data, open]);

  const isHigher = (parseFloat(advance) || 0) > (parseFloat(balance) || 0);

  const isFormValid = isMultiple
    ? movements.length > 0
    : isClose
      ? !!customer && !!comments
      : !!customer && !!advance && !!total && !!type && !!comments;

  return (
    <ModalV2
      open={open}
      onClose={onClose}
      title={title}
      toRight
      onSubmit={isFormValid ? handleSubmit : null}
      submitBtnText={submitBtnText}
      confirmColor="success"
      loading={isLoadingComp?.savingRetentionOutput}
      disabledBtn={isHigher}
    >
      <Grid item xs={12}>
        <form onSubmit={handleSearchCustomers}>
          <AutocompleteComponent
            options={customersOpt || []}
            open={customersOpt?.length > 0 && !customer}
            value={customer}
            inputValue={searchCustomer}
            filterOptions={(x) => x}
            onInputChange={(_, newInputValue, reason) => {
              if (reason !== "reset") setValues({ searchCustomer: newInputValue });
            }}
            clearOnBlur={false}
            name="customer"
            label={searching ? "Buscando..." : "Buscar cliente (Mayorista) *"}
            textFieldProps={{
              size: "small",
              placeholder: "Nombre del cliente mayorista",
              name: "searchCustomer",
              helperText: errors.customer,
              error: !!errors.customer,
            }}
            handleValues={(v) =>
              setValues({ ...v, searchCustomer: v.customer?.label || searchCustomer })
            }
            disabled={searching || !!data}
            loading={searching}
            size="small"
            onAction={
              !data ? (
                <RegularButton
                  justIcon
                  round
                  color="transparent"
                  onClick={handleSearchCustomers}
                  size="sm"
                  loading={searching}
                >
                  <SearchIcon style={{ color: blue[800] }} />
                </RegularButton>
              ) : null
            }
          />
        </form>
      </Grid>
      <Grid item xs={12} style={itemStyle}>
        <Typography variant="subtitle2" color="textSecondary">
          Retención del periodo: $ {formatNumber(retentionsTotal)}
        </Typography>
      </Grid>
      <Grid item xs={12} style={itemStyle}>
        <Typography variant="subtitle2" color="textSecondary">
          Saldo Retenciones: $ {formatNumber(balance)}
        </Typography>
      </Grid>
      <Grid item xs={12} style={itemStyle}>
        <Typography variant="subtitle2" color="textSecondary">
          Anticipos antes de esta solicitud: $ {formatNumber(paymentsTotal)}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      {!isClose && (
        <Grid item xs={12}>
          <TextField
            label="Anticipo *"
            name="advance"
            value={values.advance_ || advance}
            onChange={handleAdvanceChange}
            variant="outlined"
            size="small"
            fullWidth
            inputProps={inputRightAlign}
            error={!!errors.advance}
            helperText={errors.advance}
          />
        </Grid>
      )}
      {!isClose && isHigher && (
        <Grid item xs={12}>
          <Typography variant="caption" color="error">
            El anticipo no puede ser mayor al total de anticipos del periodo
          </Typography>
        </Grid>
      )}
      <Grid item xs={12}>
        {isClose ? (
          <Typography variant="subtitle2" style={{ textAlign: "right" }}>
            Balance: $ {formatNumber(balance)}
          </Typography>
        ) : (
          <TextField
            label="Total"
            name="total"
            value={formatNumber(total)}
            variant="outlined"
            size="small"
            fullWidth
            InputProps={{ readOnly: true }}
            inputProps={inputRightAlign}
            error={!!errors.total}
            helperText={errors.total}
          />
        )}
      </Grid>
      {!isClose && !isMultiple && (
        <Grid item xs={12}>
          <AutocompleteComponent
            options={retentionsTypesOptions.filter(t => t.value !== retentionsTypes.CLOSE)}
            value={type}
            name="type"
            label="Tipo *"
            handleValues={setValues}
            size="small"
            textFieldProps={{ size: "small", error: !!errors.type, helperText: errors.type }}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label="Comentarios *"
          name="comments"
          value={comments}
          onChange={onChange}
          variant="outlined"
          size="small"
          fullWidth
          multiline
          rows={3}
          error={!!errors.comments}
          helperText={errors.comments}
        />
      </Grid>
      {isMultiple && (
        <>
          <Grid item xs={12} style={{ display: "flex", justifyContent: "flex-end" }}>
            <RegularButton
              size="sm"
              color="info"
              round
              justIcon
              onClick={() => {
                if (!advance || !comments) return;
                setMovements((prev) => [
                  ...prev,
                  {
                    idCustomer: data?.idCustomer || customer?.idCustomer || customer?.value,
                    idAccount: data?.idAccount || customer?.idAccount,
                    idStation: station?.id,
                    type: retentionsTypes.OTHER,
                    amount: parseFloat(advance) || 0,
                    date: newDate(),
                    periodMonth: month,
                    periodYear: year,
                    description: comments || "",
                  },
                ]);
                setValues({ comments: "", advance: "", advance_: "" });
              }}
            >
              <Add style={{ margin: 0 }} />
            </RegularButton>
          </Grid>
          <Grid item xs={12}>
            <CardTable
              data={movements}
              columns={[
                { Header: "Descripción", accessor: "description" },
                { Header: "Monto", accessor: "amount", Cell: ({ value }) => <div>$ {formatNumber(value)}</div> },
                { Header: "Fecha", accessor: "date" },
              ]}
              title={`Total: $ ${formatNumber(movements.reduce((sum, m) => sum + (parseFloat(m.amount) || 0), 0))}`}
              hasHeader
              blockBtn
              defaultPageSize={5}
            />
          </Grid>
        </>
      )}
    </ModalV2>
  );
}
